import { Injectable, signal } from '@angular/core';
import dayjs from "dayjs";
import {
  PlayerContentItem,
  PlayerHost,
  PlayerMix,
  PlayerShow,
  PlayerProgram, PlayerSegment, MediaToPlay, ContentToPlay
} from '../models';
import { CreateUuid, Uuid } from '@hidat/huijs-common';

type TimelineItemTypes = TimelineSegmentItem;
type TimelineTypes = "SegmentItem" | "Underwriting" | "RelatedContent";


/**
 * A container for a timeline item.
 */
export class TimelineContainer {
  readonly addedAt: dayjs.Dayjs;
  public readonly type: TimelineTypes;
  public readonly item: TimelineItemTypes;
  public readonly expiresAt: dayjs.Dayjs;
  status = 100;

  constructor(type: TimelineTypes, item: TimelineItemTypes, expiresAt: dayjs.Dayjs) {
    this.addedAt = dayjs();
    this.type = type;
    this.item = item;
    this.expiresAt = expiresAt;
  }

  updateStatus(timeToCheck: dayjs.Dayjs = dayjs()): number {
    if (this.status >= 0 && this.expiresAt) {
      if (this.expiresAt.isBefore(timeToCheck)) {
        this.status = -100;
      }
    }
    return this.status;
  }
}

/**
 * A TimelineItem that represents a piece of content that has been played.
 * Includes information about the containing segment.
 * If 'topOfSegment' is set, then this is the first item in the segment.
 * If 'topOfMix' is set, then this is the first item in the mix.
 */
export interface TimelineSegmentItem {
  uid: string;   // Used for generating/tracking in the DOM
  content?: PlayerContentItem;
  playbackLogId?: Uuid;
  startedAt: dayjs.Dayjs;
  mix?: PlayerMix;
  program?: PlayerProgram;
  show?: PlayerShow;
  host?: PlayerHost;
  segment?: PlayerSegment;
  segmentId?: Uuid;
  topOfSegment: boolean;
  topOfShow: boolean;
  topOfMix: boolean;
  endOfSegment: boolean;
  endOfShow: boolean;
  endOfMix: boolean;
  segmentIndex: number;
}

/**
 * A normalized view of all the content items played in a segment
 */
export interface TimelineSegment {
  segmentId: Uuid | undefined;
  currentItem: TimelineSegmentItem | null;   // Current playing item, before it gets pushed into items once completed.
  items: TimelineContainer [];
}

/**
 * Timeline Service
 * This is a rethinking of the standard 'playlist', mutated into more of a social-media style timeline.
 * You can add multiple types of content to the timeline, which is then used as the source to draw the timeline on page.
 * The session will be responsible for adding the content, unlike the playlist, it does not connect directly to the playback engine.
 * Keeps at most 'maxContentSeconds' worth of data around.
 * The timeline keeps multiple publicly available lists of items, these are arrays to make change tracking simple.
 * nowPlaying: The currently playing item
 * nowPlayingSegmentItems: All the items related to the currently playing segment, except 'nowPlaying'
 * timelineItems: All the previously playing items, excluding 'nowPlaying'
 * previousSegmentItems: All the previously played items, except the currently playing segment items
 */
@Injectable({providedIn: 'root'})
export class TimelineService {
  // Now playing item and segment
  public readonly nowPlayingItem = signal<TimelineSegmentItem | undefined>(undefined);
  public readonly nowPlayingSegment = signal<TimelineSegment | undefined>(undefined);
  public readonly nowPlayingMix = signal<PlayerMix | undefined>(undefined);
  public readonly currentTimelineItems = signal<TimelineContainer []>([]);
  public readonly playedMixes = signal<PlayerMix []>([]);

  // Everything after now playing
  protected timelineItems: TimelineContainer[] = [];
  protected timelineSegments: TimelineSegment [] = [];
  protected timelineMixes: PlayerMix [] = [];

  private maxContentSeconds = 3600 * 3;  // Display up to 3 hours of playlist info


  /**
   * Records a newly-started track as the current now-playing item.
   *
   * The previous `nowPlayingItem` is promoted into `currentTimelineItems` with an expiry
   * of `startedAt + 3 hours`. Boundary flags (`topOfSegment`, `endOfSegment`, etc.) are
   * set automatically by comparing the incoming segment/show UIDs to those of the outgoing item.
   *
   * Called by `PlaybackService.onContentChanged()` every time a new track begins.
   *
   * @param contentPlaying - The `ContentToPlay` that just started.
   * @param itemPlaying - The `MediaToPlay` carrying show/host/segment metadata, or `undefined` for unscheduled content.
   * @param mix - The mix currently active, or `undefined`.
   */
  public addCurrentlyPlayingItem(contentPlaying: ContentToPlay, itemPlaying: MediaToPlay | undefined, mix: PlayerMix | undefined): void {
    console.debug('Adding currently playing to Timeline: ', contentPlaying.uid)
    const startedAt = contentPlaying.startedAt ?? dayjs();
    const segmentItem: TimelineSegmentItem = {
      uid: CreateUuid(),  // used for tracking in the DOM, want to make sure it's unique
      content: contentPlaying.content,
      playbackLogId: contentPlaying.uid,
      startedAt: startedAt,
      mix: mix,
      topOfSegment: false,
      topOfShow: false,
      topOfMix: false,
      endOfSegment: false,
      endOfShow: false,
      endOfMix: false,
      segmentIndex: 0,    // Index of the track within the current segment.  Most recent played will be 0.
    }

    if (itemPlaying) {
      segmentItem.show = itemPlaying._show;
      segmentItem.host = itemPlaying._host;
      segmentItem.segment = itemPlaying._segment;
      segmentItem.segmentId = itemPlaying._segment?.uid;
    }

    // If we currently have a now playing, then push it down into the timeline, and check if we are moving to a new segment or mix
    const nowPlaying = this.nowPlayingItem();
    if (nowPlaying) {
      const newSegmentId = segmentItem.segment?.uid;
      const newShowId = segmentItem.show?.uid;

      if (nowPlaying.segment?.uid != newSegmentId) {
        nowPlaying.endOfSegment = true;
        segmentItem.topOfSegment = true;
        if (nowPlaying.show?.uid != newShowId) {
          segmentItem.topOfShow = true;
          nowPlaying.endOfShow = true;
        }
      }
      const expiresAt = startedAt.add(this.maxContentSeconds, 's');
      this.addToTimeline('SegmentItem', nowPlaying, expiresAt, newSegmentId);
      this.nowPlayingItem.set(segmentItem);
      if (!this.nowPlayingSegment()) {
        const nowPlayingSegment = {
          segmentId: newSegmentId,
          currentItem: segmentItem,
          items: []
        };
        this.nowPlayingSegment.set(nowPlayingSegment);
      }
      const nowPlayingMix = this.nowPlayingMix();
      if (mix && mix.uid && nowPlayingMix && nowPlayingMix.uid != mix.uid) {
        this.timelineMixes = [nowPlayingMix, ...this.timelineMixes];
        this.nowPlayingMix.set(mix);
        this.playedMixes.set(this.timelineMixes);
      }
    } else {
      // First play!
      segmentItem.topOfSegment = true;
      segmentItem.topOfMix = true;
      const newPlaying = segmentItem;
      this.nowPlayingItem.set(segmentItem);
      const nowPlayingSegment = {
        segmentId: newPlaying.segmentId,
        currentItem: newPlaying,
        items: []
      };
      this.nowPlayingSegment.set(nowPlayingSegment);
      if (mix) {
        this.nowPlayingMix.set(mix);
      }
    }
  }

  /**
   * Moves the current `nowPlayingItem` into history and clears `nowPlayingItem`.
   * Called when the queue runs dry and no new track follows (e.g. mix played out).
   * Marks the item as `topOfSegment` and `topOfShow` so the UI renders a visual break.
   */
  public forceNowPlayingToPast() {
    const nowPlaying = this.nowPlayingItem();
    if (nowPlaying) {
      nowPlaying.topOfSegment = true;
      nowPlaying.topOfShow = true;
      const expiresAt = nowPlaying.startedAt.add(this.maxContentSeconds, 's');
      this.addToTimeline('SegmentItem', nowPlaying, expiresAt, undefined);
    }
    this.nowPlayingItem.set(undefined);
  }

  /**
   * Low-level timeline rebuild. Prepends `item` to the history list, regroups all items by segment,
   * recomputes `segmentIndex` values, and discards expired containers.
   * Updates `currentTimelineItems` and `nowPlayingSegment` signals on every call.
   *
   * Prefer `addCurrentlyPlayingItem()` for normal playback events — call this directly only
   * when injecting non-standard timeline entries.
   *
   * @param type - The timeline entry type (currently always `'SegmentItem'`).
   * @param item - The `TimelineSegmentItem` to add.
   * @param expiresAt - When this entry should be dropped from the timeline.
   * @param npSegmentId - Segment UID of the *incoming* now-playing item, used to split the
   *   first segment group out as `nowPlayingSegment` rather than leaving it in history.
   */
  public addToTimeline(type: TimelineTypes, item: TimelineItemTypes, expiresAt: dayjs.Dayjs, npSegmentId?: Uuid | undefined): void {
    const newTimelineItems: TimelineContainer[] = [];
    let newTimelineSegments: TimelineSegment [] = [];

    // Add the new items at the top of the list(s)
    const timelineItem = new TimelineContainer(type, item, expiresAt);
    const timeToCheck: dayjs.Dayjs = dayjs();
    let currentSegmentId: Uuid | undefined;
    let currentSegment: TimelineSegment;
    let currentIndex = 0;

    newTimelineItems.push(timelineItem);
    if (type === "SegmentItem") {
      const segmentId = item.segmentId;
      currentSegmentId = segmentId ?? npSegmentId;
    }

    currentSegment = {
      segmentId: currentSegmentId,
      currentItem: item,
      items: [timelineItem]
    }

    // Go through current timeline items and expire any that are old
    for (const ti of this.timelineItems) {
      if (ti.type == 'SegmentItem' && ti.item.segment) {
        const s = ti.item.segment;
        if (s.uid != currentSegmentId) {
          newTimelineSegments.push(currentSegment)
          currentSegmentId = s.uid;
          currentIndex = 0;
          currentSegment = {
            segmentId: currentSegmentId,
            currentItem: null,
            items: []
          }
        } else {
          currentIndex++;
        }
        currentSegment.items.push(ti);

        if (ti.expiresAt.isAfter(timeToCheck)) {
          ti.item.segmentIndex = currentIndex;
          newTimelineItems.push(ti);
        }
      }
    }

    newTimelineSegments.push(currentSegment);

    this.timelineItems = newTimelineItems;
    this.currentTimelineItems.set(newTimelineItems);
    let nowPlayingSegment: TimelineSegment | undefined = undefined;
    if (newTimelineSegments.length > 0) {
      if (npSegmentId && newTimelineSegments[0].segmentId === npSegmentId) {
        nowPlayingSegment = newTimelineSegments[0];
        newTimelineSegments = newTimelineSegments.slice(1)
      }
    }
    this.nowPlayingSegment.set(nowPlayingSegment);
    this.timelineSegments = newTimelineSegments;
  }

}
